import { useQuery } from "@apollo/client";
import { Button, Input } from "components/ui/v2";
import { ExplorePersonRowV2 } from "components/ui/v2/explore/bundle/ExplorePersonRowV2";
import { gqlMessage } from "gql";
import { Eye, EyeOff } from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useMemo, useState } from "react";

export interface StoryViewsModalV2Props {
  storyId: string;
  onViewsCountChange?: (count: number) => void;
}

const ViewSkeleton = () => (
  <div className="flex animate-pulse items-center gap-2 rounded-lg px-1 py-1.5">
    <div className="h-8 w-8 shrink-0 rounded-full bg-slate-200 dark:bg-zinc-700" />
    <div className="min-w-0 flex-1 space-y-1.5">
      <div className="h-3 w-1/3 rounded bg-slate-200 dark:bg-zinc-700" />
      <div className="h-2 w-1/2 rounded bg-slate-200 dark:bg-zinc-700" />
    </div>
  </div>
);

const EmptyState = ({
  emoji,
  message,
}: {
  emoji?: string;
  message: string;
}) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    {emoji ? <span className="mb-4 text-4xl">{emoji}</span> : null}
    <p className="text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
      {message}
    </p>
  </div>
);

const ViewsList = ({
  views,
  loading,
  searchTerm,
  t,
}: {
  views: any[];
  loading: boolean;
  searchTerm: string;
  t: (key: string) => string;
}) => {
  const filteredViews = useMemo(() => {
    if (!searchTerm) return views;
    return views.filter((view) => {
      const userName =
        `${view.user?.name || ""} ${view.user?.lastname || ""}`.toLowerCase();
      const userEmail = view.user?.email?.toLowerCase() || "";
      return (
        userName.includes(searchTerm.toLowerCase()) ||
        userEmail.includes(searchTerm.toLowerCase())
      );
    });
  }, [views, searchTerm]);

  if (loading) {
    return (
      <div className="flex flex-col gap-1">
        {Array.from({ length: 6 }).map((_, index) => (
          <ViewSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (filteredViews.length === 0 && searchTerm) {
    return (
      <EmptyState
        emoji="🔍"
        message={t("No users found matching your search")}
      />
    );
  }

  if (filteredViews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <EyeOff className="mb-4 h-10 w-10 text-slate-400 dark:text-zinc-500" />
        <p className="text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
          {t("Nadie ha visto esta story aún")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {filteredViews.map((view) => {
        const seenLabel = `${t("Visto el")} ${moment(view.seenAt).format("DD/MM/YYYY HH:mm")}`;

        return (
          <ExplorePersonRowV2
            key={view.user?.id}
            user={view.user}
            trailing={
              <span className="hidden max-w-[140px] truncate text-[9px] text-slate-400 dark:text-zinc-500 sm:inline">
                <Eye className="mr-1 inline h-3 w-3" />
                {seenLabel}
              </span>
            }
          />
        );
      })}
    </div>
  );
};

export function StoryViewsModalV2({
  storyId,
  onViewsCountChange,
}: StoryViewsModalV2Props) {
  const { t } = useTranslation("explore");
  const { t: tCommon } = useTranslation("common");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, loading } = useQuery(gqlMessage.queries.GET_STORY_VIEWS, {
    fetchPolicy: "network-only",
    variables: { storyId },
    skip: !storyId,
  });

  const views = useMemo(() => {
    return data?.getStoryViewers || [];
  }, [data?.getStoryViewers]);

  useEffect(() => {
    onViewsCountChange?.(views.length);
  }, [onViewsCountChange, views.length]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="w-full">
      {!loading && views.length > 0 ? (
        <div className="mb-4">
          <div className="relative">
            <Input
              placeholder={tCommon("Search users...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              className="pr-8"
              aria-label={tCommon("Search users by name or email")}
            />
            {searchTerm ? (
              <Button
                variant="ghost"
                size="xs"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={clearSearch}
                aria-label={tCommon("Clear search")}
              >
                ✕
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <section role="region" aria-label={`${views.length} story views`}>
        <ViewsList
          views={views}
          loading={loading}
          searchTerm={searchTerm}
          t={(key) => (key === "Nadie ha visto esta story aún" ? t(key) : tCommon(key))}
        />
      </section>
    </div>
  );
}

export default StoryViewsModalV2;
